import { useRef } from 'react';
import { Box } from '@mui/material';
import { Options } from 'highcharts';
import { Highchart } from '@diana/core';
import HighchartsReact from 'highcharts-react-official';
import { convertStreamDataToChartData } from '../../utils/convertStreamDataToChart';
import { isEmpty } from 'lodash';
import { IStreamDataPoint, IProfileData } from '../../types/api/profile';
import NoDataDisplay from '../NoDataDisplay/NoDataDisplay';
import LinearProgress from '../LinearProgress/LinearProgress';
import { useFilterOptions } from '../../stores/useFilterOptions';

interface LineChartProps {
  dataKey: keyof IStreamDataPoint;
  options?: Options;
  data: IProfileData[];
  loading: boolean;
  noDataDisplay?: {
    title?: string;
    subtitle?: string;
  };
  color?: string;
}

export const LineChart: React.FC<LineChartProps> = ({
  dataKey,
  options,
  data,
  loading,
  noDataDisplay,
  color,
}) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const devicesById = useFilterOptions((state) => state.devicesById);
  const chartSeries = convertStreamDataToChartData(data || [], dataKey, devicesById);

  const chartOptions: Options = {
    chart: {
      type: 'line',
    },
    title: {
      text: '',
    },
    xAxis: {
      type: 'datetime',
      crosshair: true,
      title: {},
      dateTimeLabelFormats: {
        minute: '%H:%M', // Format for time
      },
    },
    series: chartSeries.map((seriesData) => ({
      name: seriesData.name,
      type: 'line',
      data: seriesData.data,
      ...(color && { color }), // Only add if color exists
    })),
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    },
    tooltip: {
      //headerFormat: '<b>{series.name} </b><br />',
      //pointFormat: `{series.name}: {point.y} MT <br>`,
      shared: true,
      backgroundColor: '#fff',
      borderColor: '#E4E4E4',
      borderRadius: 10,
      borderWidth: 1,
    },
    time: {
      useUTC: false,
    },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '280px',
        overflow: 'auto hidden',
      }}
      className="line-chart"
    >
      {isEmpty(chartOptions.series) && !loading ? (
        <NoDataDisplay title={noDataDisplay?.title} subtitle={noDataDisplay?.subtitle} />
      ) : (
        <Box sx={{ position: 'absolute', width: '100%', height: '100%' }}>
          <LinearProgress loading={loading} />
          <Highchart ref={chartComponentRef} options={{ ...chartOptions, ...options }} />
        </Box>
      )}
    </Box>
  );
};
